import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const DayGrid = ({ weekdays }) => (
  <div className="grid grid-cols-7 gap-1.5" data-testid="emotion-weekday-grid">
    {weekdays.map((w) => (
      <div key={w.day} className="mm-soft !rounded-xl p-2 text-center min-w-0" data-testid={`emotion-weekday-${w.day.toLowerCase()}`}>
        <div className="text-[11px] font-semibold text-mm-muted">{w.day}</div>
        <div className="text-xl sm:text-2xl my-1">{w.top ? w.top.emoji : "·"}</div>
        <div className="text-[10px] sm:text-xs truncate">{w.top ? w.top.name : "—"}</div>
        {w.top && <div className="text-[10px] text-mm-primary font-semibold">{w.top.share}%</div>}
      </div>
    ))}
  </div>
);

const Heatmap = ({ rows }) => {
  const max = Math.max(1, ...rows.flatMap((r) => r.counts));
  return (
    <div className="overflow-x-auto mm-scroll-x" data-testid="emotion-heatmap">
      <table className="w-full text-xs border-separate border-spacing-1">
        <thead><tr><th />{DAYS.map((d) => <th key={d} className="font-semibold text-mm-muted">{d}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} data-testid={`emotion-heatmap-row-${slug(r.name)}`}>
              <td className="pr-2 whitespace-nowrap">{r.emoji} {r.name}</td>
              {r.counts.map((c, i) => (
                <td key={DAYS[i]} title={`${r.name} on ${DAYS[i]}: ${c}`} className="h-7 min-w-[28px] rounded-md text-center font-semibold"
                  style={{ background: c ? `color-mix(in srgb, var(--mm-primary) ${15 + Math.round((85 * c) / max)}%, transparent)` : "var(--mm-surface-light)", color: c / max > 0.5 ? "#fff" : "var(--mm-text)" }}>
                  {c || ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const EmotionWeekdays = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/insights/emotion-weekdays?tz=${encodeURIComponent(tz())}`).then((r) => setD(r.data))
      .catch((e) => { logError("Weekday emotions load failed:", e); setD({ weekdays: [], heatmap: [], total: 0 }); });
  }, []);
  if (!d) return null;
  return (
    <div className="mm-card p-5 space-y-4" data-testid="emotion-weekdays">
      <div>
        <h3 className="font-semibold">Your week in emotions</h3>
        <p className="text-xs text-mm-muted">Your most-picked emotion on each weekday, from the last 8 weeks</p>
      </div>
      {d.total ? (
        <>
          <DayGrid weekdays={d.weekdays} />
          {d.tip && <p className="text-sm mm-soft p-3" data-testid="emotion-weekday-tip">{d.tip}</p>}
          <Heatmap rows={d.heatmap} />
        </>
      ) : <p className="text-sm text-mm-muted" data-testid="emotion-weekdays-empty">Pick emotions in your check-ins or journal and your weekly patterns will show up here.</p>}
    </div>
  );
};
